/*
 * te-video-drag-fix.js  (v2)
 * Tackle Express - product media video playback fix (Eurus theme / Splide 4.1.3)
 *
 * Fixes two independent bugs on the product media gallery:
 *
 *   1. Dragging the native <video> progress bar starts a Splide carousel drag
 *      instead of seeking. Splide binds touchstart/mousedown on the track in the
 *      CAPTURE phase, so it wins the gesture before the video ever sees it, then
 *      preventDefault()s the moves - which is why scrubbing either does nothing
 *      or flips to the next media item.
 *
 *   2. Each video sits in a wrapper carrying
 *      @click.prevent="$store.xVideo.mp4Thumbnail($el)". Clicks on the native
 *      controls bubble to that wrapper and toggle play/pause a second time, so
 *      the native play/pause button appears dead.
 *
 * v2 note: v1 looked the Splide instance up via `root.splide`. Splide 4.1.3 does
 * NOT set that property, so the lookup always returned null and the drag fix was
 * a no-op. v2 hooks Splide.prototype.mount to keep its own registry, and also
 * pushes video selectors into each slider's `noDrag` option as a second line of
 * defence.
 *
 * Safe to load on every page. No dependencies. Idempotent.
 */
(function () {
  'use strict';

  if (window.__teVideoDragFix) return;
  window.__teVideoDragFix = true;

  var VIDEO_NODRAG = 'video, .video, .product-media--video';

  /* ------------------------------------------------------------------ *
   * 0. Registry - Splide 4.1.3 does not expose instances on the root
   * ------------------------------------------------------------------ */

  function hookSplide() {
    var S = window.Splide;
    if (typeof S !== 'function') return false;
    var proto = S.prototype;
    if (!proto || proto.__teHooked) return true;

    var origMount = proto.mount;
    if (typeof origMount !== 'function') return false;

    proto.mount = function () {
      var result = origMount.apply(this, arguments);
      try {
        if (this.root) this.root.__teSplide = this;
        // Second line of defence: teach the slider itself to ignore video.
        // The setter emits "updated", which makes Drag re-read the option.
        var current = (this.options && this.options.noDrag) || '';
        if (current.indexOf('video') === -1) {
          this.options = { noDrag: current ? current + ', ' + VIDEO_NODRAG : VIDEO_NODRAG };
        }
      } catch (err) {
        /* never let our patch break a slider */
      }
      return result;
    };

    proto.__teHooked = true;
    return true;
  }

  if (!hookSplide()) {
    // splide4.1.3.js is deferred too - retry briefly until it lands.
    var tries = 0;
    var poll = setInterval(function () {
      if (hookSplide() || ++tries > 100) clearInterval(poll);
    }, 50);
    document.addEventListener('DOMContentLoaded', hookSplide);
    window.addEventListener('load', hookSplide);
  }

  /* ------------------------------------------------------------------ *
   * 1. Keep Splide's drag off the video's native controls
   * ------------------------------------------------------------------ */

  var held = [];

  function videoFromEvent(e) {
    var t = e.target;
    if (!t || t.nodeType !== 1) return null;
    // Native control interactions are retargeted to the <video> host by the
    // shadow DOM, so target is normally the video itself. closest() is a fallback.
    if (t.tagName === 'VIDEO') return t;
    return t.closest ? t.closest('video') : null;
  }

  function splideFor(el) {
    var root = el.closest ? el.closest('.splide') : null;
    while (root) {
      if (root.__teSplide) return root.__teSplide;
      if (root.splide) return root.splide;
      root = root.parentElement && root.parentElement.closest
        ? root.parentElement.closest('.splide')
        : null;
    }
    return null;
  }

  function hold(e) {
    var video = videoFromEvent(e);
    if (!video) return;

    var splide = splideFor(video);
    if (!splide || !splide.Components) return;

    var drag = splide.Components.Drag;
    if (!drag || typeof drag.disable !== 'function') return;

    var wasDraggable = !splide.options || splide.options.drag !== false;

    for (var i = 0; i < held.length; i++) {
      if (held[i].drag === drag) return;
    }

    drag.disable(true);
    held.push({ drag: drag, wasDraggable: wasDraggable });
  }

  function release() {
    if (!held.length) return;
    // Defer so the in-flight gesture fully unwinds before Splide re-arms.
    setTimeout(function () {
      while (held.length) {
        var entry = held.pop();
        if (!entry.wasDraggable) continue;
        try {
          entry.drag.disable(false);
        } catch (err) {
          /* slider was destroyed mid-gesture - nothing to restore */
        }
      }
    }, 0);
  }

  // Capture on document fires before Splide's capture listener on the track.
  document.addEventListener('pointerdown', hold, true);
  document.addEventListener('mousedown', hold, true);
  document.addEventListener('touchstart', hold, true);

  document.addEventListener('pointerup', release, true);
  document.addEventListener('pointercancel', release, true);
  document.addEventListener('mouseup', release, true);
  document.addEventListener('touchend', release, true);
  document.addEventListener('touchcancel', release, true);
  window.addEventListener('blur', release);

  /* ------------------------------------------------------------------ *
   * 2. Stop the wrapper's @click toggle from fighting the native controls
   * ------------------------------------------------------------------ */

  function stopClick(e) {
    e.stopPropagation();
  }

  function armVideo(video) {
    if (video.__teVideoArmed) return;
    if (!video.hasAttribute('controls')) return;
    // Only inside the product media gallery - leave hero/background video alone.
    if (!video.closest('.external-video, .product-media--video, .splide')) return;

    video.__teVideoArmed = true;
    video.addEventListener('click', stopClick, false);
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    var vids = root.querySelectorAll('video[controls]');
    for (var i = 0; i < vids.length; i++) armVideo(vids[i]);
  }

  function boot() {
    scan(document);

    // Media slides are injected lazily (x-intersect, zoom modal, variant swaps).
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'VIDEO') armVideo(node);
          else scan(node);
        }
      }
    });

    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
